/**
 * Admin Dashboard API Service Layer
 * Handles all API calls from frontend to admin endpoints
 */

import { getAuthHeaders } from './authService';
import { API_ENDPOINTS } from './config/apiConfig';

const ADMIN_API_BASE = API_ENDPOINTS.ADMIN_BASE;

/**
 * Helper function to handle API responses
 */
async function handleResponse(response) {
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'An error occurred');
  }

  return data;
}

// ============= DASHBOARD STATS =============

/**
 * Get dashboard overview statistics
 */
export async function getDashboardStats() {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_API_BASE}/stats`, { headers });
  return handleResponse(response);
}

// ============= USER MANAGEMENT - STUDENTS =============

/**
 * Get all students with optional filtering
 * @param {object} filters - { search, grade_level, onboarding_completed, limit, offset }
 */
export async function getAllStudents(filters = {}) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams(filters);
  const response = await fetch(`${ADMIN_API_BASE}/students?${params}`, { headers });
  return handleResponse(response);
}

// ============= USER MANAGEMENT - TEACHERS =============

/**
 * Get all teachers with optional filtering
 * @param {object} filters - { search, confirmed, limit, offset }
 */
export async function getAllTeachers(filters = {}) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams(filters);
  const response = await fetch(`${ADMIN_API_BASE}/teachers?${params}`, { headers });
  return handleResponse(response);
}

/**
 * Approve or reject a teacher
 * @param {string} teacherId - Teacher ID
 * @param {boolean} approved - Whether to approve or reject
 * @param {string} reason - Reason for approval/rejection
 */
export async function approveTeacher(teacherId, approved, reason = '') {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_API_BASE}/teachers/${teacherId}/approve`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ approved, reason })
  });
  return handleResponse(response);
}

// ============= USER MANAGEMENT - INSTITUTES =============

/**
 * Get all institutes with optional filtering
 * @param {object} filters - { search, limit, offset }
 */
export async function getAllInstitutes(filters = {}) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams(filters);
  const response = await fetch(`${ADMIN_API_BASE}/institutes?${params}`, { headers });
  return handleResponse(response);
}

/**
 * Approve or reject an institute
 * @param {string} instituteId - Institute ID
 * @param {boolean} approved - true to approve, false to reject
 * @param {string} reason - Reason for approval/rejection
 */
export async function approveInstitute(instituteId, approved, reason = '') {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_API_BASE}/institutes/${instituteId}/approve`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ approved, reason })
  });
  return handleResponse(response);
}

// ============= USER STATUS MANAGEMENT =============

/**
 * Update user status (suspend/activate)
 * @param {string} userId - User ID
 * @param {string} userType - 'student', 'teacher', or 'institute'
 * @param {string} status - 'active' or 'suspended'
 * @param {string} reason - Reason for status change
 */
export async function updateUserStatus(userId, userType, status, reason) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_API_BASE}/users/${userId}/status`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ userType, status, reason })
  });
  return handleResponse(response);
}

/**
 * Delete a user account
 * @param {string} userId - User ID
 * @param {string} userType - 'student', 'teacher', or 'institute'
 * @param {string} reason - Reason for deletion
 */
export async function deleteUser(userId, userType, reason) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_API_BASE}/users/${userId}`, {
    method: 'DELETE',
    headers,
    body: JSON.stringify({ userType, reason })
  });
  return handleResponse(response);
}

// ============= BOOKING MANAGEMENT =============

/**
 * Get all bookings with optional filtering
 * @param {object} filters - { status, date_from, date_to, search, limit, offset }
 */
export async function getAllBookings(filters = {}) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams(filters);
  const response = await fetch(`${ADMIN_API_BASE}/bookings?${params}`, { headers });
  return handleResponse(response);
}

// ============= ANALYTICS =============

/**
 * Get daily user signup statistics
 * @param {string} startDate - Start date (ISO format)
 * @param {string} endDate - End date (ISO format)
 */
export async function getUserGrowthData(startDate, endDate) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
  const response = await fetch(`${ADMIN_API_BASE}/analytics/user-growth?${params}`, { headers });
  return handleResponse(response);
}

/**
 * Get revenue statistics by period
 * @param {string} startDate - Start date (ISO format)
 * @param {string} endDate - End date (ISO format)
 * @param {string} period - 'day', 'week', or 'month'
 */
export async function getRevenueData(startDate, endDate, period = 'day') {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    period
  });
  const response = await fetch(`${ADMIN_API_BASE}/analytics/revenue?${params}`, { headers });
  return handleResponse(response);
}

/**
 * Get top performing teachers
 * @param {number} limit - Number of teachers to return
 * @param {string} metric - 'bookings' or 'revenue'
 */
export async function getTopTeachers(limit = 10, metric = 'bookings') {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({ limit: limit.toString(), metric });
  const response = await fetch(`${ADMIN_API_BASE}/analytics/top-teachers?${params}`, { headers });
  return handleResponse(response);
}

// ============= SECURITY & AUDIT =============

/**
 * Get security events with filtering
 * @param {object} filters - { severity, event_type, limit, offset }
 */
export async function getSecurityEvents(filters = {}) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams(filters);
  const response = await fetch(`${ADMIN_API_BASE}/security/events?${params}`, { headers });
  return handleResponse(response);
}

/**
 * Get admin action audit log
 * @param {object} filters - { admin_id, action_type, resource_type, limit, offset }
 */
export async function getAdminActions(filters = {}) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams(filters);
  const response = await fetch(`${ADMIN_API_BASE}/security/admin-actions?${params}`, { headers });
  return handleResponse(response);
}

/**
 * Get Supabase Security Advisor recommendations
 * Fetches security checks and recommendations from Supabase
 */
export async function getSupabaseSecurityAdvisor() {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_API_BASE}/security/supabase-advisor`, { headers });
  return handleResponse(response);
}

/**
 * Get Supabase authentication audit logs
 * Fetches all authentication events from Supabase's audit log
 * @param {object} filters - { action, success, limit, offset }
 */
export async function getSupabaseAuthLogs(filters = {}) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams(filters);
  const response = await fetch(`${ADMIN_API_BASE}/security/auth-logs?${params}`, { headers });
  return handleResponse(response);
}

// ============= HEALTH CHECK =============

/**
 * Check admin API health status
 */
export async function checkAdminHealth() {
  try {
    const response = await fetch(`${ADMIN_API_BASE}/health`);
    return handleResponse(response);
  } catch (error) {
    return {
      status: 'unhealthy',
      error: error.message
    };
  }
}

// ============= HELPER FUNCTIONS =============

/**
 * Check if current user is an admin
 */
export async function checkAdminStatus() {
  try {
    const result = await getDashboardStats();
    return result.success === true;
  } catch (error) {
    return false;
  }
}

/**
 * Format date for API calls
 */
export function formatDateForAPI(date) {
  if (typeof date === 'string') return date;
  return date.toISOString();
}

/**
 * Get date range for analytics
 * @param {number} days - Number of days back from today
 */
export function getDateRange(days = 30) {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  return {
    startDate: formatDateForAPI(startDate),
    endDate: formatDateForAPI(endDate)
  };
}

// ============= REWARDS & GAMIFICATION =============

/**
 * Get all level rewards (1-100)
 * Returns configured rewards and empty slots for levels without rewards
 */
export async function getLevelRewards() {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_API_BASE}/rewards/levels`, { headers });
  return handleResponse(response);
}

/**
 * Set or update reward for a specific level
 * @param {number} level - Level number (1-100)
 * @param {object} rewardData - { reward_type, reward_value?, reward_label?, is_active }
 */
export async function setLevelReward(level, rewardData) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_API_BASE}/rewards/levels/${level}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(rewardData)
  });
  return handleResponse(response);
}

/**
 * Delete reward from a specific level
 * @param {number} level - Level number (1-100)
 */
export async function deleteLevelReward(level) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_API_BASE}/rewards/levels/${level}`, {
    method: 'DELETE',
    headers
  });
  return handleResponse(response);
}

/**
 * Get all wallet items for monitoring
 * @param {object} filters - { student_id?, status?, limit, offset }
 */
export async function getWalletItems(filters = {}) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams(filters);
  const response = await fetch(`${ADMIN_API_BASE}/rewards/wallet-items?${params}`, { headers });
  return handleResponse(response);
}

// ============= TEACHER PAYOUT MANAGEMENT =============

/**
 * Get all teacher payout requests with optional filtering
 * @param {string} statusFilter - Filter by payout status (pending, processing, completed, cancelled)
 * @param {number} page - Page number for pagination
 * @param {number} limit - Items per page
 */
export async function getAdminPayouts(statusFilter = null, page = 1, limit = 20) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({ page, limit });

  if (statusFilter) {
    params.append('status', statusFilter);
  }

  const response = await fetch(`${ADMIN_API_BASE}/payouts?${params}`, { headers });
  return handleResponse(response);
}

/**
 * Get payout statistics for admin dashboard
 */
export async function getPayoutStats() {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_API_BASE}/payouts/stats`, { headers });
  return handleResponse(response);
}

/**
 * Get detailed view of a single payout request
 * @param {string} payoutId - Payout request ID
 */
export async function getPayoutDetails(payoutId) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_API_BASE}/payouts/${payoutId}`, { headers });
  return handleResponse(response);
}

/**
 * Mark payout as processing (admin started reviewing)
 * @param {string} payoutId - Payout request ID
 * @param {string} notes - Optional processing notes
 */
export async function processPayoutRequest(payoutId, notes = '') {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_API_BASE}/payouts/${payoutId}/process`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ notes })
  });
  return handleResponse(response);
}

/**
 * Mark payout as completed/paid
 * @param {string} payoutId - Payout request ID
 * @param {string} notes - Optional payment reference or notes
 */
export async function completePayoutRequest(payoutId, notes = '') {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_API_BASE}/payouts/${payoutId}/complete`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ notes })
  });
  return handleResponse(response);
}

/**
 * Reject payout request
 * @param {string} payoutId - Payout request ID
 * @param {string} reason - Rejection reason (required)
 * @param {string} notes - Optional additional notes
 */
export async function rejectPayoutRequest(payoutId, reason, notes = '') {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_API_BASE}/payouts/${payoutId}/reject`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ reason, notes })
  });
  return handleResponse(response);
}

/**
 * Get pending payments (paid bookings not yet paid out to teachers)
 * Grouped by teacher for easy payout creation
 * @param {string} teacherId - Optional filter by teacher ID
 * @param {number} limit - Items per page (default: 50)
 * @param {number} offset - Offset for pagination (default: 0)
 */
export async function getPendingPayments(teacherId = null, limit = 50, offset = 0) {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({ limit, offset });

  if (teacherId) {
    params.append('teacher_id', teacherId);
  }

  const response = await fetch(`${ADMIN_API_BASE}/payments/pending?${params}`, { headers });
  return handleResponse(response);
}

/**
 * Create a payout for selected bookings
 * @param {object} payoutData - { teacher_email, booking_ids[], bank_account_details?, notes? }
 */
export async function createPayoutFromBookings(payoutData) {
  const headers = await getAuthHeaders();
  const response = await fetch(`${ADMIN_API_BASE}/payments/create-payout`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payoutData)
  });
  return handleResponse(response);
}
